import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { tabulatorGet } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { DateTime } from 'luxon';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MdSearch, MdSearchOff } from "react-icons/md";
import { EditIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon, RefreshIcon } from "../svg/svgIcons";
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { MachineStateDot } from '../../components/StatusIndicator';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { getEffectiveMask } from '../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  clearHeaderFilterForField,
  normalizeColumnVisibilityForSave,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  applyCompanyListColumnLayout,
  restoreCompanyListUserMaskToTable,
  mergePagedMachineOutageStateListFiltersAndSorters,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getPusherChannel } from '../../utils/pusherChannel';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../utils/tabulatorIdColumn';
import { buildAuditTabulatorColumns, mergeColumnVisibilityWithAuditDefaults } from '../../utils/tabulatorAuditColumns';
import { LocaleContext } from '../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../utils/printChrome';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
import { uiT } from '../../utils/uiTranslate';

const MASK_PAGE = 'MaintenanceMachineStateList';

const MAINTENANCE_MACHINE_STATE_LIST_COLUMN_TITLE_KEYS = {
  id: 'MachineList.Column.Id',
  name: 'MachineList.Column.Name',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  type: 'MachineList.Column.Type',
  serial_number: 'MachineList.Column.SerialNumber',
  fabrication_year: 'MachineList.Column.FabricationYear',
  last_overhaul: 'MachineList.Column.LastOverhaul',
  description: 'MachineList.Column.Description',
  manufacturer: 'MachineList.Column.Manufacturer',
  manufacturer_address: 'MachineList.Column.ManufacturerAddress',
  manufacturer_tel_number: 'MachineList.Column.ManufacturerTel',
  manufacturer_fax: 'MachineList.Column.ManufacturerFax',
  manufacturer_email: 'MachineList.Column.ManufacturerEmail',
  manufacturer_web: 'MachineList.Column.ManufacturerWeb',
  currentState: 'ServiceTask.MachineState',
  stateSince: 'MaintenanceMachineStateList.Column.StateSince',
  activity: 'MaintenanceMachineStateList.Column.OverallStatus',
  created_at: 'MachineList.Column.CreatedAt',
  updated_at: 'MachineList.Column.UpdatedAt',
};

function applyLiveColumnTitles(table, t, filterOptions = {}, printCtx = null) {
  if (!table?.getColumns) return;
  const headerFilters = {
    'machine_type.name': {
      placeholder: t('Common.All'),
      options: filterOptions.machineTypeFilterOptions,
    },
  };
  applyTabulatorRuntimeI18n(table, t, {
    columnTitleKeys: MAINTENANCE_MACHINE_STATE_LIST_COLUMN_TITLE_KEYS,
    headerFilters,
    reformatRows: true,
    dataLoaderLoading: t('Common.LoadingData'),
    dataLoaderError: t('Common.LoadFailed'),
    onRefreshCells: (tbl) => {
      if (!tbl?.options) return;
      const chromeOpts = {
        branding: printCtx?.branding,
        productFallbackSrc: defaultProductLogoSrc,
        companySettingsPrint: printCtx?.companySettingsPrint,
        moduleSettings: printCtx?.moduleSettings,
        title: t('MaintenanceMachineStateList.PrintHeader'),
        printDateLabel: t('Print.DateLabel'),
      };
      tbl.options.printHeader = buildCanonicalPrintHeaderHtml(chromeOpts);
      tbl.options.printFooter = buildCanonicalPrintFooterHtml(chromeOpts);
    },
  });
}
const MACHINE_STATE_LABELS = { in_fault: 'V poruche', working_limited: uiT('Common.WorkingLimited'), functional: uiT('Common.Functional') };

const DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  'machine_type.name': true,
  type: true,
  serial_number: true,
  fabrication_year: true,
  last_overhaul: true,
  description: true,
  manufacturer: true,
  manufacturer_address: true,
  manufacturer_tel_number: true,
  manufacturer_fax: true,
  manufacturer_email: true,
  manufacturer_web: true,
  currentState: true,
  stateSince: true,
  activity: true,
  created_at: true,
  updated_at: true,
});

/** Poradie dátových stĺpcov (po technických + ID) pre mask layout. */
const MAINTENANCE_MACHINE_STATE_LIST_DATA_FIELD_ORDER = Object.freeze([
  'id',
  'name',
  'machine_type.name',
  'type',
  'serial_number',
  'fabrication_year',
  'last_overhaul',
  'description',
  'manufacturer',
  'manufacturer_address',
  'manufacturer_tel_number',
  'manufacturer_fax',
  'manufacturer_email',
  'manufacturer_web',
  'currentState',
  'stateSince',
  'activity',
  'created_at',
  'updated_at',
]);

function mergeMaintenanceMachineStateListColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY, saved);
}

function applyMaintenanceMachineStateListDataColumnVisibility(table, visibility) {
  if (!table) return;
  for (const field of Object.keys(DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) {
      clearHeaderFilterForField(table, field);
      table.hideColumn?.(field);
    } else {
      table.showColumn?.(field);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function normalizeMachineStateListText(value) {
  return (value ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function machineStateListTextHeaderFilter(headerValue, rowValue) {
  if (!headerValue) return true;
  return normalizeMachineStateListText(rowValue).includes(normalizeMachineStateListText(headerValue));
}

export default function MaintenanceMachineStateList({ preview = null }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const tRef = useRef(t);
  tRef.current = t;
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    getUserColor,
    getUserInitials,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState({});

  const ref = useRef(null);
  const tabulatorInstanceRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const getTable = useCallback(
    () => tabulatorInstanceRef.current ?? ref.current?.table ?? ref.current,
    []
  );
  const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, tRef.current('Common.LoadingData')), [getTable]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const refetch = () => { captureHScroll(); refreshFromStart(); };
    channel.bind('service_task.created', refetch);
    channel.bind('service_task.updated', refetch);
    channel.bind('service_task.deleted', refetch);
    return () => {
      channel.unbind('service_task.created', refetch);
      channel.unbind('service_task.updated', refetch);
      channel.unbind('service_task.deleted', refetch);
    };
  }, [captureHScroll, refreshFromStart]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
    if (extra.size == null) extra.size = pageSizePc;
    mergePagedMachineOutageStateListFiltersAndSorters(extra, {
      getTable,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: MASK_PAGE,
      useMaskFromRedux: !preview,
      searchValue,
      preview,
    });
    return extra;
  }, [pageSizePc, getTable, searchValue, preview, state.user_mask_filters]);

  function searchTable() {
    setSearchIconColor('active');
    captureHScroll();
    refreshFromStart();
    setVisibleSearch(false);
  }

  const onSelectRow = () => {
    setEditIcon(true);
    const sel = getTable()?.getSelectedData?.() ?? [];
    if (sel.length === 1) {
      setEditIcon(false);
    }
  };

  function onEditRow(machine) {
    dispatch({
      type: 'create_new_tab', name: t('Print.Title.MaintenanceMachineStateDetail'), translationKey: 'Print.Title.MaintenanceMachineStateDetail', legacyLabel: t('Print.Title.MaintenanceMachineStateDetail'),
      page: 'MaintenanceMachineStateDetail',
      props: { machine },
      mask: 'mntn038',
    });
  }

  function editEmployee() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function MachineTypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const name = rowData?.machine_type?.name ?? rowData?.machine_type_name ?? '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={name || undefined}>{name || '—'}</span>;
  }

  function StateSinceFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const dt = rowData.stateSince;
    if (!dt) return '—';
    const parsed = DateTime.fromISO(String(dt));
    return parsed.isValid ? parsed.toFormat(dateTimeFormatGlob) : '—';
  }

  function StavStrojaFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const taskLike = { type: 'Error', machine_state: rowData.currentState };
    const label = MACHINE_STATE_LABELS[rowData.currentState] || t('Common.Functional');
    return (
      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
        <MachineStateDot task={taskLike} />
        <span className="truncate" title={label}>{label}</span>
      </div>
    );
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        textHeaderFilter: machineStateListTextHeaderFilter,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials]
  );

  const machineTypeFilterOptions = useMemo(
    () => (state.machine_types || []).map((mt) => ({ label: mt.name, value: mt.name })),
    [state.machine_types]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      buildTabulatorIdColumn({
        frozen: true,
        headerFilterFunc: machineStateListTextHeaderFilter,
      }),
      {
        title: t('MachineList.Column.Name'),
        field: 'name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.Name')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.MachineTypeName'),
        field: 'machine_type.name',
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.MachineTypeName'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<MachineTypeFormatter />),
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: machineTypeFilterOptions },
        headerFilterFunc: () => true,
      },
      {
        title: t('MachineList.Column.Type'),
        field: 'type',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.Type')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.SerialNumber'),
        field: 'serial_number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.SerialNumber')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.FabricationYear'),
        field: 'fabrication_year',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.FabricationYear')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.LastOverhaul'),
        field: 'last_overhaul',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.LastOverhaul')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.Description'),
        field: 'description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.Description')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.Manufacturer'),
        field: 'manufacturer',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.Manufacturer')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.ManufacturerAddress'),
        field: 'manufacturer_address',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.ManufacturerAddress')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.ManufacturerTel'),
        field: 'manufacturer_tel_number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.ManufacturerTel')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.ManufacturerFax'),
        field: 'manufacturer_fax',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.ManufacturerFax')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.ManufacturerEmail'),
        field: 'manufacturer_email',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.ManufacturerEmail')),
        widthGrow: 1,
      },
      {
        title: t('MachineList.Column.ManufacturerWeb'),
        field: 'manufacturer_web',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: machineStateListTextHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.ManufacturerWeb')),
        widthGrow: 1,
      },
      {
        title: t('ServiceTask.MachineState'),
        field: 'currentState',
        minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceTask.MachineState'), 'multiselect'),
        widthGrow: 1,
        formatter: reactFormatter(<StavStrojaFormatter />),
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('ServiceTask.MachineState'),
          valueType: 'string',
          options: [
            { label: 'V poruche', value: 'in_fault' },
            { label: t('Common.WorkingLimited'), value: 'working_limited' },
            { label: t('Common.Functional'), value: 'functional' },
          ],
        },
        headerFilterFunc: () => true,
      },
      {
        title: t('MaintenanceMachineStateList.Column.StateSince'),
        field: 'stateSince',
        minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceMachineStateList.Column.StateSince')),
        widthGrow: 1,
        formatter: reactFormatter(<StateSinceFormatter />),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: () => true,
      },
      {
        title: t('MaintenanceMachineStateList.Column.OverallStatus'),
        field: 'activity',
        minWidth: tabulatorColumnMinWidthFromTitle(t('MaintenanceMachineStateList.Column.OverallStatus'), 'multiselect'),
        widthGrow: 1,
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: {
          placeholder: t('MaintenanceMachineStateList.Column.OverallStatus'),
          options: [
            { label: t('Common.Active'), value: 'Aktívny' },
            { label: t('Status.Excluded'), value: 'Vyradený' },
          ],
        },
        headerFilterFunc: () => true,
      },
      ...auditTailColumns,
    ],
    [machineTypeFilterOptions, auditTailColumns, t]
  );

  useEffect(() => {
    if (!tableBuiltRef.current) return;
    applyLiveColumnTitles(getTable(), t, { machineTypeFilterOptions }, {
      branding: state?.branding,
      companySettingsPrint: state?.company_settings_print,
      moduleSettings: state?.module_settings,
    });
  }, [t, locale, getTable, columns, machineTypeFilterOptions, state?.branding, state?.company_settings_print, state?.module_settings]);

  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);

  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeMaintenanceMachineStateListColumnVisibilityFromSaved),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineStateListColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(() => {
    const normalized = normalizeColumnVisibilityForSave(
      DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      columnsVisible
    );
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({
        severity: 'warn',
        summary: 'Maska',
        detail: t('Common.SelectAtLeastOneColumn'),
        life: 4000,
      });
      return;
    }
    const table = getTable();
    applyMaintenanceMachineStateListDataColumnVisibility(table, normalized);
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(MASK_PAGE, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  }, [columnsVisible, getTable, setUserMaskFilter, toast]);

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineStateListColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        applyCompanyListColumnLayout(table, attributes, MAINTENANCE_MACHINE_STATE_LIST_DATA_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
        setColumnsVisible(colsForSave);
        applyMaintenanceMachineStateListDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.clearAlert?.();
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast, captureHScroll, refreshFromStart]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateListDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateListDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineStateListDataColumnVisibility,
    }),
    [getTable]
  );

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button className="disabled:opacity-25" disabled={editIcon} onClick={() => editEmployee()} type="button">
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); captureHScroll(); refreshFromStart(); }}
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.Search')} />
              ) : (
                <MdSearchOff fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.Search')} />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={() => getTable()?.download?.('xlsx', 'vypadky_strojov.xlsx', { sheetName: t('Maintenance.MachineOutages') })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" onClick={() => scrollToFirstUtil(getTable())}><BackToFirstIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" onClick={() => scrollUpUtil(getTable())}><BackIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" onClick={() => scrollDownUtil(getTable())}><ForwardIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" onClick={() => scrollToLastUtil(getTable())}><ForwardToLastIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog(t('Maintenance.MachineOutages'), MASK_PAGE)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => {
                  ref.current = r.current;
                  tabulatorInstanceRef.current = r.current?.table ?? r.current;
                }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    if (!preview) {
                      const effective = getEffectiveMask(MASK_PAGE, state);
                      if (effective?.type === 'user' && effective.userMask) {
                        const m = effective.userMask;
                        isRestoringRef.current = true;
                        restoreCompanyListUserMaskToTable(table, m, {
                          canonicalFieldOrder: MAINTENANCE_MACHINE_STATE_LIST_DATA_FIELD_ORDER,
                          forceFrozenId: true,
                          setColumnsVisible,
                          clearHeaderFiltersWhenEmpty: true,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeMaintenanceMachineStateListColumnVisibilityFromSaved(jc);
                            setVis(merged);
                            applyMaintenanceMachineStateListDataColumnVisibility(tbl, merged);
                          },
                        });
                        const merged = mergeMaintenanceMachineStateListColumnVisibilityFromSaved(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_MAINTENANCE_MACHINE_STATE_LIST_COLUMN_VISIBILITY)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        isRestoringRef.current = true;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        applyCompanyListColumnLayout(table, attributes, MAINTENANCE_MACHINE_STATE_LIST_DATA_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
                        if (jsonColumns && typeof jsonColumns === 'object') {
                          const merged = mergeMaintenanceMachineStateListColumnVisibilityFromSaved(jsonColumns);
                          setColumnsVisible(merged);
                          applyMaintenanceMachineStateListDataColumnVisibility(table, merged);
                        }
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                      }
                    }
                    tableBuiltRef.current = true;
                    applyLiveColumnTitles(table, t, { machineTypeFilterOptions }, {
                      branding: state?.branding,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                    });
                  },
                  ...getTabulatorPersistLayoutEvents(MASK_PAGE, tabulatorInstanceRef, setUserMaskFilter),
                  headerFilterChanged: () => {
                    if (!isRestoringRef.current) captureHScroll();
                  },
                  sortChanged: () => {
                    if (!isRestoringRef.current) captureHScroll();
                  },
                  renderComplete: () => {
                    restoreHScroll();
                  },
                }}
                options={{
                  layout: 'fitColumns',
                  renderHorizontal: 'virtual',
                  movableColumns: true,
                  headerSortTristate: true,
                  rowHeight: 42,
                  ajaxURL: '/api/machines/outage-state/paged',
                  ajaxParams: () => ({}),
                  ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
                  dataLoader: true,
                  dataLoaderLoading: t('Common.LoadingData'),
                  dataLoaderError: t('Common.LoadFailed'),
                  progressiveLoad: 'scroll',
                  progressiveLoadScrollMargin: 80,
                  paginationSize: pageSizePc,
                  filterMode: 'remote',
                  sortMode: 'remote',
                  pagination: false,
                  ajaxRequesting: () => {
                    captureHScroll();
                    const table = getTable();
                    if (table?.initialized) table.alert?.(tRef.current('Common.LoadingData'));
                    return true;
                  },
                  ajaxResponse: (url, params, response) => {
                    getTable()?.clearAlert?.();
                    return response;
                  },
                  ajaxError: () => {
                    getTable()?.clearAlert?.();
                  },
                  printAsHtml: true,
                  printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('MaintenanceMachineStateList.PrintHeader'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                  printRowRange: 'active',
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button type="button" onClick={() => searchTable()} className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mmsl-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`mmsl-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
